import React from 'react';
import { useButtonLinks } from '../hooks/useButtonLinks';
import { useTranslation } from '../hooks/useTranslation';

const PrimeAccount = () => {
  const { handleButtonClick } = useButtonLinks();
  const { t } = useTranslation();
  return (
    <div className="prime-account-page">
      {/* Hero Section */}
      <section className="prime-hero">
        <div className="container">
          <div className="prime-hero-content">
            <h1 className="prime-title">{t('primeAccount.hero.title')}</h1>
            <p className="prime-subtitle">
              {t('primeAccount.hero.subtitle')}
            </p>
            <div className="hero-question">
              <h2>{t('primeAccount.hero.question')}</h2>
            </div>
            <div className="prime-features">
              <div className="feature-highlight">
                <i className="fas fa-history"></i>
                <span>{t('primeAccount.hero.features.journey')}</span>
              </div>
              <div className="feature-highlight">
                <i className="fas fa-users"></i>
                <span>{t('primeAccount.hero.features.partners')}</span>
              </div>
              <div className="feature-highlight">
                <i className="fas fa-tools"></i>
                <span>{t('primeAccount.hero.features.tools')}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What You'll Get Section */}
      <section className="prime-benefits">
        <div className="container">
          <div className="benefits-header">
            <h2 className="section-title">{t('primeAccount.benefits.title')}</h2>
            <p className="section-subtitle">{t('primeAccount.benefits.subtitle')}</p>
          </div>
          
          <div className="benefits-grid">
            <div className="benefit-item">
              <i className="fas fa-chart-line"></i>
              <p>{t('primeAccount.benefits.items.spreads')}</p>
            </div>
            <div className="benefit-item">
              <i className="fas fa-globe"></i>
              <p>{t('primeAccount.benefits.items.instruments')}</p>
            </div>
            <div className="benefit-item">
              <i className="fas fa-mobile-alt"></i>
              <p>{t('primeAccount.benefits.items.platform')}</p>
            </div>
            <div className="benefit-item">
              <i className="fas fa-bolt"></i>
              <p>{t('primeAccount.benefits.items.deposits')}</p>
            </div>
            <div className="benefit-item">
              <i className="fas fa-chart-bar"></i>
              <p>{t('primeAccount.benefits.items.charting')}</p>
            </div>
            <div className="benefit-item">
              <i className="fas fa-graduation-cap"></i>
              <p>{t('primeAccount.benefits.items.education')}</p>
            </div>
            <div className="benefit-item">
              <i className="fas fa-headset"></i>
              <p>{t('primeAccount.benefits.items.support')}</p>
            </div>
            <div className="benefit-item">
              <i className="fas fa-database"></i>
              <p>{t('primeAccount.benefits.items.data')}</p>
            </div>
            <div className="benefit-item">
              <i className="fas fa-shield-alt"></i>
              <p>{t('primeAccount.benefits.items.protection')}</p>
            </div>
          </div>

          <div className="benefits-cta">
            <p className="cta-text">{t('primeAccount.benefits.cta')}</p>
            <button 
              className="btn btn-primary"
              onClick={(e) => handleButtonClick('getStarted', e)}
            >
              {t('primeAccount.benefits.button')}
            </button>
          </div>
        </div>
      </section>

      {/* Specifications Section */}
      <section className="prime-specifications">
        <div className="container">
          <h2 className="section-title">{t('primeAccount.specifications.title')}</h2>
          <div className="specs-grid">
            <div className="spec-card">
              <div className="spec-item">
                <span className="spec-label">{t('accountShared.specifications.spread')}</span>
                <span className="spec-value">{t('primeAccount.specifications.spread')}</span>
              </div>
              <div className="spec-item">
                <span className="spec-label">{t('accountShared.specifications.commission')}</span>
                <span className="spec-value">{t('primeAccount.specifications.commission')}</span>
              </div>
              <div className="spec-item">
                <span className="spec-label">{t('accountShared.specifications.leverage')}</span>
                <span className="spec-value">{t('primeAccount.specifications.leverage')}</span>
              </div>
              <div className="spec-item">
                <span className="spec-label">{t('accountShared.specifications.minDeposit')}</span>
                <span className="spec-value">{t('primeAccount.specifications.minDeposit')}</span>
              </div>
              <div className="spec-item">
                <span className="spec-label">{t('accountShared.specifications.minOrder')}</span>
                <span className="spec-value">{t('primeAccount.specifications.minOrder')}</span>
              </div>
              <div className="spec-item">
                <span className="spec-label">{t('accountShared.specifications.stopOut')}</span>
                <span className="spec-value">{t('primeAccount.specifications.stopOut')}</span>
              </div>
              <div className="spec-item">
                <span className="spec-label">{t('accountShared.specifications.platform')}</span>
                <span className="spec-value">{t('primeAccount.specifications.platform')}</span>
              </div>
              <div className="spec-item">
                <span className="spec-label">{t('accountShared.specifications.instruments')}</span>
                <span className="spec-value">{t('primeAccount.specifications.instruments')}</span>
              </div>
            </div>

            <div className="spec-card">
              <div className="spec-item">
                <span className="spec-label">Markets</span>
                <span className="spec-value">All markets</span>
              </div>
              <div className="spec-item">
                <span className="spec-label">Expert Advisory</span>
                <span className="spec-value">Allowed</span>
              </div>
              <div className="spec-item">
                <span className="spec-label">Minimum order volume (lots)</span>
                <span className="spec-value">0.01</span>
              </div>
              <div className="spec-item">
                <span className="spec-label">Technical analysis tools</span>
                <span className="spec-value">Yes</span>
              </div>
              <div className="spec-item">
                <span className="spec-label">Min Deposit</span>
                <span className="spec-value">$250</span>
              </div>
              <div className="spec-item">
                <span className="spec-label">Live news</span>
                <span className="spec-value">Yes</span>
              </div>
            </div>

            <div className="spec-card">
              <div className="spec-item">
                <span className="spec-label">Maximum number of positions</span>
                <span className="spec-value">No Limitations</span>
              </div>
              <div className="spec-item">
                <span className="spec-label">Free market data</span>
                <span className="spec-value">Yes</span>
              </div>
              <div className="spec-item">
                <span className="spec-label">Swap</span>
                <span className="spec-value">Free*</span>
              </div>
              <div className="spec-item">
                <span className="spec-label">24/6 phone support</span>
                <span className="spec-value">Yes</span>
              </div>
              <div className="spec-item">
                <span className="spec-label">Stop-out level</span>
                <span className="spec-value">20%</span>
              </div>
              <div className="spec-item">
                <span className="spec-label">Educational videos and guides</span>
                <span className="spec-value">Yes</span>
              </div>
            </div>

            <div className="spec-card">
              <div className="spec-item">
                <span className="spec-label">Advanced charting packages</span>
                <span className="spec-value">Yes</span>
              </div>
              <div className="spec-item">
                <span className="spec-label">In-person seminars and webinars</span>
                <span className="spec-value">Yes</span>
              </div>
              <div className="spec-item">
                <span className="spec-label">Platforms available</span>
                <span className="spec-value">MT5</span>
              </div>
              <div className="spec-item">
                <span className="spec-label">News and articles from River Prime</span>
                <span className="spec-value">Yes</span>
              </div>
            </div>
          </div>
          <p className="specs-note">* T&C Applied.</p>
        </div>
      </section>

      {/* Financial Move Section */}
      <section className="financial-move">
        <div className="container">
          <h2 className="section-title">{t('primeAccount.financialMove.title')}</h2>
        </div>
      </section>

      {/* Risk Warning Section */}
      <section className="risk-warning">
        <div className="container">
          <div className="warning-content">
            <div className="warning-header">
              <h3>{t('primeAccount.riskWarning.excited')}</h3>
              <p>{t('primeAccount.riskWarning.subtitle')}</p>
            </div>
            <div className="warning-points">
              <div className="warning-item">
                <i className="fas fa-exclamation-triangle"></i>
                <p>{t('primeAccount.riskWarning.warnings.risk')}</p>
              </div>
              <div className="warning-item">
                <i className="fas fa-shield-alt"></i>
                <p>{t('primeAccount.riskWarning.warnings.management')}</p>
              </div>
              <div className="warning-item">
                <i className="fas fa-lightbulb"></i>
                <p>{t('primeAccount.riskWarning.warnings.demo')}</p>
              </div>
              <div className="warning-item">
                <i className="fas fa-gift"></i>
                <p>{t('primeAccount.riskWarning.warnings.gift')}</p>
              </div>
            </div>
            <div className="warning-cta">
              <button 
                className="btn btn-primary"
                onClick={(e) => handleButtonClick('openAccount', e)}
              >
                {t('primeAccount.riskWarning.createButton')}
              </button>
              <button className="btn btn-secondary">{t('primeAccount.riskWarning.tourButton')}</button>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Help Section */}
      <section className="quick-help">
        <div className="container">
          <h2 className="section-title">{t('accountShared.quickHelp.title')}</h2>
          <div className="help-sections">
            <div className="help-column">
              <h3>{t('accountShared.quickHelp.experienced.title')}</h3>
              <div className="help-items">
                <div className="help-item">
                  <h4>{t('accountShared.quickHelp.experienced.partner.title')}</h4>
                  <p>{t('accountShared.quickHelp.experienced.partner.description')}</p>
                </div>
                <div className="help-item">
                  <h4>{t('accountShared.quickHelp.experienced.markets.title')}</h4>
                  <p>{t('accountShared.quickHelp.experienced.markets.description')}</p>
                </div>
                <div className="help-item">
                  <h4>{t('accountShared.quickHelp.experienced.events.title')}</h4>
                  <p>{t('accountShared.quickHelp.experienced.events.description')}</p>
                </div>
              </div>
            </div>
            
            <div className="help-column">
              <h3>{t('accountShared.quickHelp.newTrader.title')}</h3>
              <div className="help-items">
                <div className="help-item">
                  <h4>{t('accountShared.quickHelp.newTrader.platform.title')}</h4>
                  <p>{t('accountShared.quickHelp.newTrader.platform.description')}</p>
                </div>
                <div className="help-item">
                  <h4>{t('accountShared.quickHelp.newTrader.charts.title')}</h4>
                  <p>{t('accountShared.quickHelp.newTrader.charts.description')}</p>
                </div>
                <div className="help-item">
                  <h4>{t('accountShared.quickHelp.newTrader.demo.title')}</h4>
                  <p>{t('accountShared.quickHelp.newTrader.demo.description')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrimeAccount;